import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchVerdicts } from '../services/verdictService';

const CACHE_TTL_MS = 60 * 1000;
const verdictCache = new Map();

const getCacheKey = (bodyId, filtersKey) => `${bodyId || 'unknown'}::${filtersKey}`;

const defaultPagination = {
  currentPage: 1,
  totalPages: 1,
  itemsPerPage: 10,
  hasNextPage: false,
  hasPreviousPage: false
};

export const useVerdicts = (bodyId, filters) => {
  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);
  const cacheKey = useMemo(() => getCacheKey(bodyId, filtersKey), [bodyId, filtersKey]);
  const [data, setData] = useState({ verdicts: [], total: 0, pagination: defaultPagination });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const loadVerdicts = useCallback(async ({ useCache = true } = {}) => {
    if (!bodyId) return;

    const cached = verdictCache.get(cacheKey);
    if (useCache && cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchVerdicts({
        bodyId,
        page: filters?.page,
        limit: filters?.limit,
        search: filters?.search,
        outcome: filters?.outcome,
        date: filters?.date,
        signal: controller.signal
      });

      const nextData = {
        verdicts: response.verdicts || [],
        total: response.total || 0,
        pagination: response.pagination || defaultPagination
      };

      verdictCache.set(cacheKey, { data: nextData, timestamp: Date.now() });
      setData(nextData);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Unable to load verdicts');
    } finally {
      setLoading(false);
    }
  }, [bodyId, cacheKey, filters]);

  useEffect(() => {
    loadVerdicts({ useCache: true });

    return () => {
      abortRef.current?.abort();
    };
  }, [loadVerdicts]);

  const refetch = useCallback(() => loadVerdicts({ useCache: false }), [loadVerdicts]);

  return {
    verdicts: data.verdicts,
    total: data.total,
    pagination: data.pagination,
    loading,
    error,
    refetch
  };
};

export default useVerdicts;
